import EMPLOYEE_VALIDATION, { CAREER_REVIEW_VALIDATION } from './employeeValidation';
import { useKernelApi, usePeopleManagementApi } from 'infrastructure/api';
import { ResourceId, useCrudService, useTimelineService } from 'framework/crud';
import useAuthenticatedUserService, { UserName } from 'infrastructure/security/authentication/authenticatedUserService';
import { useSecurityFormMapper } from 'framework/form';
import { useSearchParameters } from 'framework/routing';
import { EMPLOYEE_STATE_KEYS, EMPLOYEE_WORKPLACE_LOCATION_IDS, RESOURCE_URL } from './constants';
import { EMPLOYEE_MY_PAGE_PARAMETER } from './route/constants';
import { EMPLOYEE_TYPES_ALIAS } from './constants';
import useEmployeeSuggestionsService from './employeeSuggestionsService';
import useEmployeeNamesService from './employeeNames/employeeNamesService';
import {
  DOCUMENTS_ALIAS,
  UPDATE_EMPLOYEE_ACCOUNTS_1C_ID,
  UPDATE_EMPLOYEE_ACCOUNTS_AZURE_ID,
  UPDATE_EMPLOYEE_MAIN_HR_INFORMATION,
  UPDATE_EMPLOYEE_SPECIALIZATION_INFO,
} from './model';
import { getIdentityDocumentNumber, mapSearchFilters, mapSortingParameters, splitFamily } from './utils';
import useEmployeeMapper from './employeeMapper';
import { loadEmployeePermissionsOnce } from './permissions/employeePermissionsCache';
import { useGlobalSecurityReactContext } from 'framework/authorization';

const useEmployeeService = () => {
  const { getUserAlias } = useAuthenticatedUserService();
  const {
    createMapper,
    updateMapper,
    personalInfoMapper,
    specializationInfoMapper,
    mentorshipInfoMapper,
    identityDocumentsMapper,
    contactInfoMapper,
    accountsMapper,
    familyInfoMapper,
    peopleManagementMapper,
  } = useEmployeeMapper();
  const { sendGetRequest, sendPutRequest, sendPatchRequest } = useKernelApi();
  const { sendGetRequest: sendPeopleManagementGetRequest, sendPatchRequest: sendPeopleManagementPatchRequest } =
    usePeopleManagementApi();
  const { search: searchSuggestions, searchWithContractors, advancedSearch } = useEmployeeSuggestionsService();
  const searchParams = useSearchParameters();
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const globalPermissions = useGlobalSecurityReactContext()?.permissions;

  const accountsPermissions = {
    azureId: [UPDATE_EMPLOYEE_ACCOUNTS_AZURE_ID],
    extendedInformation: {
      financialInformation: [UPDATE_EMPLOYEE_ACCOUNTS_1C_ID],
    },
  };

  const { filterForm: filterAccountsForm } = useSecurityFormMapper(accountsPermissions);
  const { filterForm: filterSpecializationForm } = useSecurityFormMapper({
    specializationInfo: [UPDATE_EMPLOYEE_MAIN_HR_INFORMATION, UPDATE_EMPLOYEE_SPECIALIZATION_INFO],
    positionId: [UPDATE_EMPLOYEE_MAIN_HR_INFORMATION, UPDATE_EMPLOYEE_SPECIALIZATION_INFO],
  });

  const {
    create,
    update,
    getById: initialGetById,
    invalidateCache,
    ...baseMethods
  } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useKernelApi,
  });

  const {
    search: searchTimelineItemsBase,
    create: createTimelineItemBase,
    getTimelinePageById,
  } = useTimelineService({
    parentEntityUrl: `${RESOURCE_URL}/:id`,
    useService: useKernelApi,
  });

  const getGlobalPeopleManagementPermissions = async (): Promise<string[]> => {
    try {
      const response = await sendPeopleManagementGetRequest(`/permissions`);
      return await response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getGlobalPeopleManagementPermissions: Failed to fetch global permissions:', error);
      return [];
    }
  };

  const getEmployeePermissions = async (id: ResourceId): Promise<string[]> => {
    return loadEmployeePermissionsOnce(id, () => baseMethods.getPermissions(id));
  };

  const getServiceWidePermissions = async (): Promise<string[]> => {
    const loadedPermissions = Object.keys(globalPermissions || {});

    return loadedPermissions.length ? loadedPermissions : getGlobalPeopleManagementPermissions();
  };

  const getCombinedFromServicesEmployeePermissions = async (id: ResourceId): Promise<string[]> => {
    try {
      const results = await Promise.allSettled([getEmployeePermissions(id), getServiceWidePermissions()]);

      return results.flatMap(result =>
        result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [],
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('getCombinedFromServicesEmployeePermissions: Error while combining permissions:', error);
      return [];
    }
  };

  const createTimelineItem = async (employeeId: StringOrNumber, timelineEntityType: string, data: any) => {
    return createTimelineItemBase(employeeId, timelineEntityType, {
      ...data,
      employeeId,
    });
  };

  const searchTimelineItems = async (
    employeeId: StringOrNumber,
    requestData: Parameters<typeof searchTimelineItemsBase>[1] = {},
  ) => {
    try {
      const response = await searchTimelineItemsBase(employeeId, requestData);

      return {
        pageNumber: response?.pageNumber ?? 0,
        totalPages: response?.totalPages ?? 0,
        result: response?.result || [],
      };
    } catch {
      return {
        pageNumber: 0,
        totalPages: 0,
        result: [],
      };
    }
  };

  const pinEmployeeTimeline = async (employeeId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => {
    const request = await sendPatchRequest(`${RESOURCE_URL}/${employeeId}/timeline/${timelineId}`, {
      isPinned: pinStatus,
    });

    return request.json();
  };

  const search = ({ pageNumber = 0, sort = { 'nameEn.lastName': 'ASC' }, filter = {} } = {}) => {
    const request = {
      filter: mapSearchFilters(filter),
      sort: mapSortingParameters(sort),
      pageSize: 30,
      pageNumber,
    };

    return baseMethods.search(request);
  };

  const searchAll = ({ sort = { 'nameEn.lastName': 'ASC' }, filter = {} } = {}) => {
    const params = {
      filter: mapSearchFilters(filter),
      sort: mapSortingParameters(sort),
    };

    return baseMethods.searchAll(params);
  };

  const getCurrentEmployeeId = async (employeeId: StringOrNumber) => {
    const alias = await getUserAlias();
    return employeeId === EMPLOYEE_MY_PAGE_PARAMETER ? alias : employeeId;
  };

  const getValidationSchema = () => Promise.resolve(EMPLOYEE_VALIDATION);

  const getCareerReviewValidationSchema = () => Promise.resolve(CAREER_REVIEW_VALIDATION);

  const checkIsNewEmployee = (state: string) => EMPLOYEE_STATE_KEYS.NEW === state;

  const getFullName = (userName?: Nullable<Partial<UserName>>) => {
    const { firstName = '', lastName = '', maidenName = '' } = userName || {};
    return `${firstName} ${lastName} ${maidenName ? `(${maidenName})` : ''}`.trim();
  };

  const getFullNameWithMiddleName = (userName?: Nullable<Partial<UserName>>) => {
    const { firstName = '', lastName = '', maidenName = '', middleName = '' } = userName || {};
    return `${firstName} ${middleName || ''} ${lastName} ${maidenName ? `(${maidenName})` : ''} `.trim();
  };

  const getFullNameWithoutMaiden = (userName?: Nullable<Partial<UserName>>) => {
    const { firstName = '', lastName = '' } = userName || {};
    return `${firstName} ${lastName}`.trim();
  };

  const getById = async (params?: string, ignoreCash = false) => {
    const alias = await getUserAlias();
    const employeeData = await initialGetById((params || alias) as string, ignoreCash);
    const newData = { ...employeeData };

    let documentType;
    if (newData.extendedInformation.passportData) {
      documentType = DOCUMENTS_ALIAS.PASSPORT;
    } else if (newData.extendedInformation.residencePermitData) {
      documentType = DOCUMENTS_ALIAS.RESIDENCE_PERMIT;
    } else if (newData.extendedInformation.peselData) {
      documentType = DOCUMENTS_ALIAS.PESEL;
    } else {
      documentType = undefined;
    }
    newData.extendedInformation.documentType = documentType;
    if (newData.mentorshipInformation?.mentees) {
      if (
        newData.mentorshipInformation.mentees.length > 0 &&
        typeof newData.mentorshipInformation.mentees[0] === 'object' &&
        newData.mentorshipInformation.mentees[0].alias
      ) {
        newData.mentorshipInformation.mentees = newData.mentorshipInformation.mentees.map(
          (item: { alias: string }) => item.alias,
        );
      }
    }

    if (
      newData.employeeType === EMPLOYEE_TYPES_ALIAS.CONTRACTOR &&
      newData.initializationState === EMPLOYEE_STATE_KEYS.FINALIZED
    ) {
      newData.initializationState = null;
    }

    return newData;
  };

  const mapDataForUpdate = async (employeeData: Record<any, any>) => {
    const mapData = { ...employeeData };
    const mentorData = await getEmployeeNamesByAliases([employeeData.mentorshipInformation.mentor]);
    const menteesData = await getEmployeeNamesByAliases(employeeData.mentorshipInformation.mentees);
    const mentorName = mentorData[0] ? getFullName(mentorData[0].nameEn) : '';

    const familyData = splitFamily(employeeData.extendedInformation.familyMembers);
    mapData.extendedInformation.children = familyData.children;
    mapData.extendedInformation.other = familyData.other;

    mapData.azureId = searchParams.azureId || employeeData.azureId;
    mapData.workingEmail = searchParams.workingEmail || employeeData.workingEmail;
    mapData.mentorshipInformation.mentorName = mentorName;
    mapData.mentorshipInformation.mentees = menteesData;

    return mapData;
  };

  const mapMentorshipInformationAfterUpdate = async (
    initialEmployeeData: Record<any, any>,
    updatedMentorshipInfo: Record<any, any>,
  ) => {
    const mapData = { ...initialEmployeeData };

    const mentorData = await getEmployeeNamesByAliases([updatedMentorshipInfo.mentor]);
    const mentorName = mentorData[0] ? getFullName(mentorData[0].nameEn) : '';

    const menteesData = await getEmployeeNamesByAliases(updatedMentorshipInfo.mentees);

    mapData.mentorshipInformation.mentor = updatedMentorshipInfo.mentor;
    mapData.mentorshipInformation.mentorName = mentorName;
    mapData.mentorshipInformation.mentees = menteesData;

    return mapData;
  };

  const getForUpdate = async (id: StringOrNumber, ignoreCash = false) => {
    const initEmployeeData = await getById(id as string, ignoreCash);
    const employeeData = await mapDataForUpdate(initEmployeeData);

    return employeeData;
  };

  const getCurrentEmployee = async () => {
    const employee = await getById();
    return employee;
  };

  const isWorkplaceLocationNotRemote = (location: string) => location !== EMPLOYEE_WORKPLACE_LOCATION_IDS.REMOTE_ONLY;

  /**
   * Returns employee ID by alias.
   *
   * @param {Nullable<string>} employeeAlias employee alias (ex: E1462487)
   * @returns {Promise<number>} employee ID
   */
  const getEmployeeCompanyId = async (employeeAlias: string) => {
    const employeeDto = await getById(employeeAlias);
    return employeeDto.companyId;
  };

  const getEmployeeName = async (employeeAlias: string) => {
    const employeeName = await sendGetRequest(`/employees/${employeeAlias}/name`);
    return employeeName.json();
  };

  const getBasicEmployeeById = async (employeeId: string) => {
    const employee = await sendGetRequest(`/employees/${employeeId}`);
    return employee.json();
  };

  const updatePersonalInfo = async (id: StringOrNumber, personalInfoData: any) => {
    const mappedData = personalInfoMapper(personalInfoData);
    const response = await update(id, mappedData);
    return response.info || response;
  };

  const updateSpecializationInfo = async (id: StringOrNumber, specializationInfoData: any) => {
    const url = `${RESOURCE_URL}/${id}`;
    const filteredData = filterSpecializationForm(specializationInfoData);
    const mappedData = specializationInfoMapper(filteredData);
    const response = await sendPutRequest(url, mappedData);
    const result = await response.json();
    return result.info || result;
  };

  const updateMentorshipInfo = async (id: StringOrNumber, mentorshipInfoData: any) => {
    const url = `${RESOURCE_URL}/${id}`;
    const mappedData = mentorshipInfoMapper(mentorshipInfoData);
    const response = await sendPeopleManagementPatchRequest(url, mappedData);
    const result = await response.json();
    return result.info || result;
  };

  const updateIdentityDocuments = async (id: StringOrNumber, identityDocumentsData: any) => {
    const mappedData = identityDocumentsMapper(identityDocumentsData);
    const response = await update(id, mappedData);
    return response.info || response;
  };

  const updateContactInfo = async (id: StringOrNumber, contactInfoData: any) => {
    const mappedData = contactInfoMapper(contactInfoData);
    const response = await update(id, mappedData);
    return response.info || response;
  };

  const updateRequisites = async (id: StringOrNumber, data: any) => {
    const response = await update(id, {
      extendedInformation: {
        requisitesInformation: {
          ...data?.extendedInformation?.requisitesInformation,
        },
      },
    });
    return response.info || response;
  };

  const updateAccounts = async (id: StringOrNumber, accountsData: any) => {
    const filteredAccountsData = filterAccountsForm(accountsData);

    const url = `${RESOURCE_URL}/${id}`;
    const mappedData = accountsMapper(filteredAccountsData);

    const response = await sendPutRequest(url, mappedData);
    const result = await response.json();
    return result.info || result;
  };

  const updateFamilyInfo = async (id: StringOrNumber, familyInfoData: any) => {
    const mappedData = familyInfoMapper(familyInfoData);
    const response = await update(id, mappedData);
    return response.info || response;
  };

  const updatePeopleManagement = async (id: StringOrNumber, peopleManagementData: any) => {
    const url = `${RESOURCE_URL}/${id}`;
    const mappedData = peopleManagementMapper(peopleManagementData);
    const response = await sendPutRequest(url, mappedData);
    const result = await response.json();
    return result.info || result;
  };

  const settingsMapper = (settingsData: any) => {
    const { photosIds, extendedInformation, ...rest } = settingsData;

    const mappedData = { ...rest, extendedInformation: { ...extendedInformation } };

    if (photosIds && photosIds.length > 0) {
      const firstPhoto = photosIds[0];
      if (typeof firstPhoto === 'object' && firstPhoto.artifactId) {
        mappedData.mainPhotoId = firstPhoto.artifactId;
      } else if (typeof firstPhoto === 'string') {
        mappedData.mainPhotoId = firstPhoto;
      }

      mappedData.extendedInformation.photosIds = photosIds.map((photo: any) =>
        typeof photo === 'object' && photo.artifactId ? photo.artifactId : photo,
      );
    }

    return mappedData;
  };

  const updateSettings = async (id: StringOrNumber, settingsData: any) => {
    const url = `${RESOURCE_URL}/${id}`;
    const mappedData = settingsMapper(settingsData);
    const response = await sendPutRequest(url, mappedData);
    const result = await response.json();
    invalidateCache();
    return result.info || result;
  };

  return {
    ...baseMethods,
    create: (employee: any) => {
      return create(createMapper(employee));
    },
    update: (id: StringOrNumber, employee: any) => {
      return update(id, updateMapper(employee));
    },
    mapDataForUpdate,
    rawUpdate: update,
    getForUpdate,
    getCurrentEmployee,
    getCurrentEmployeeId,
    search,
    getValidationSchema,
    checkIsNewEmployee,
    getFullName,
    getFullNameWithMiddleName,
    getFullNameWithoutMaiden,
    searchSuggestions,
    getById,
    searchTimelineItems,
    createTimelineItem,
    searchAll,
    advancedSearch,
    getTimelinePageById,
    isWorkplaceLocationNotRemote,
    getCareerReviewValidationSchema,
    getIdentityDocumentNumber,
    searchWithContractors,
    getEmployeeCompanyId,
    getEmployeeName,
    getBasicEmployeeById,
    pinEmployeeTimeline,
    getEmployeePermissions,
    getGlobalPeopleManagementPermissions,
    getCombinedFromServicesEmployeePermissions,
    updatePersonalInfo,
    updateSpecializationInfo,
    updateMentorshipInfo,
    updateIdentityDocuments,
    updateContactInfo,
    updateAccounts,
    updateFamilyInfo,
    updatePeopleManagement,
    updateSettings,
    updateRequisites,
    mapMentorshipInformationAfterUpdate,
  };
};

export default useEmployeeService;
